import React from "react";
import { motion } from "framer-motion";
import { Factory, Sailboat, MapPin, TrendingUp } from "lucide-react";
import { Reveal, Eyebrow, EASE } from "./shared";

const CARDS = [
  {
    slug: "industrial",
    icon: Factory,
    title: "Industrial Strength",
    lead: "45+",
    leadLabel: "industrial estates",
    text: (
      <>
        Home to <span className="font-semibold" style={{ color: "#3B2C82" }}>45+ industrial estates</span> and over{" "}
        <span className="font-semibold" style={{ color: "#3B2C82" }}>US$4.65&nbsp;billion</span> in merchandise exports (FY25).
      </>
    ),
  },
  {
    slug: "tourism",
    icon: Sailboat,
    title: "Tourism Economy",
    lead: "Resilient",
    leadLabel: "visitor demand",
    text: (
      <>
        A resilient tourism sector attracting consistent visitor traffic from Gujarat, Maharashtra
        and across India, supporting demand across hospitality, retail and consumer businesses.
      </>
    ),
  },
  {
    slug: "location",
    icon: MapPin,
    title: "Strategic Location",
    lead: "110–170 km",
    leadLabel: "to Surat & Mumbai",
    text: (
      <>
        Located approximately <span className="font-semibold" style={{ color: "#3B2C82" }}>110&nbsp;km from Surat</span> and{" "}
        <span className="font-semibold" style={{ color: "#3B2C82" }}>170&nbsp;km from Mumbai</span>, providing access to two of
        India’s largest consumer and industrial markets.
      </>
    ),
  },
  {
    slug: "investment",
    icon: TrendingUp,
    title: "Investment Climate",
    lead: "US$181M",
    leadLabel: "FDI inflows",
    text: (
      <>
        Nearly <span className="font-semibold" style={{ color: "#3B2C82" }}>US$181&nbsp;million</span> in FDI inflows and close to{" "}
        <span className="font-semibold" style={{ color: "#3B2C82" }}>36,000 registered MSMEs</span>, reflecting sustained business
        confidence and entrepreneurial activity.
      </>
    ),
  },
];

export const WhyDNHDD = () => {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-28 lg:py-40"
      style={{ backgroundColor: "#FAF9F7" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl">
          <Reveal><Eyebrow>Why DNHDD</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-bold leading-[1.08] text-3xl sm:text-4xl lg:text-5xl" style={{ color: "#2B2B2B" }}>
              The <span style={{ color: "#3B2C82" }}>DNHDD Advantage</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} as="p" className="mt-7 text-base lg:text-lg leading-relaxed" style={{ color: "#8C8C8C" }}>
            Every market has its own story. The Union Territory of Dadra &amp; Nagar Haveli and Daman
            &amp; Diu is one of India’s most distinctive commercial ecosystems, where industrial
            growth, tourism and strategic geography converge to create opportunities that extend
            well beyond its size. For businesses looking to establish a meaningful regional
            presence, DNHDD offers a unique combination of accessibility, economic momentum and
            long-term growth potential.
          </Reveal>
        </div>

        {/* Opportunity cards */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.slug}
                data-testid={`why-card-${c.slug}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.12 }}
                whileHover={{ y: -6 }}
                className="group flex flex-col p-8 rounded-lg min-h-[300px] transition-shadow duration-300"
                style={{ backgroundColor: "#FFFFFF", border: "1px solid #D8D8D8", boxShadow: "0 1px 2px rgba(43,43,43,0.03)" }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 18px 40px -18px rgba(31,26,56,0.22)")}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 2px rgba(43,43,43,0.03)")}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-md mb-7"
                  style={{ border: "1px solid #D8D8D8", color: "#2B2B2B" }}
                >
                  <Icon size={22} strokeWidth={1.4} />
                </div>

                <div className="mb-4">
                  <p className="text-2xl font-bold leading-none" style={{ color: "#3B2C82" }}>{c.lead}</p>
                  <p className="mt-1 eyebrow text-[10px]" style={{ color: "#8C8C8C" }}>{c.leadLabel}</p>
                </div>

                <h3 className="text-lg font-bold mb-3" style={{ color: "#2B2B2B" }}>{c.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#8C8C8C" }}>{c.text}</p>

                <span
                  className="mt-auto pt-6 h-0.5 w-0 group-hover:w-10 transition-[width] duration-500"
                  style={{ background: "#F5821F" }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Source caption */}
        <Reveal delay={0.1} as="p" className="mt-6 text-xs" style={{ color: "#8C8C8C" }}>
          Source: IBEF, CEIC &amp; Government of India publications (2026)
        </Reveal>

        {/* Closing statement */}
        <div className="mt-20 lg:mt-28 pt-14 lg:pt-16" style={{ borderTop: "1px solid #D8D8D8" }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <span className="h-px w-10 block mb-6" style={{ background: "#F5821F" }} />
              <h3 className="font-bold leading-[1.12] text-2xl sm:text-3xl lg:text-[2.4rem]" style={{ color: "#2B2B2B" }}>
                Where Others See a Region,<br className="hidden sm:block" />{" "}
                <span style={{ color: "#3B2C82" }}>We See Opportunity.</span>
              </h3>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-7 lg:pt-2">
              <p className="text-lg lg:text-xl leading-relaxed" style={{ color: "#8C8C8C" }}>
                At Fortim, we believe understanding a market goes beyond data. It requires local
                insight, lasting relationships and the ability to execute with confidence.
              </p>
              <p className="mt-5 text-xl lg:text-2xl font-semibold leading-snug" style={{ color: "#3B2C82" }}>
                Fortim exists to bridge that gap.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
